// src/hooks/usePlans.js
// =============================================================================
// Single source of truth for plan data — fetched from GET /subscriptions/plans
// which reads directly from the subscription_plans DB table.
//
// NO hardcoded prices anywhere in this file.
// To change a price: update the DB (via PricingAdmin or SQL migration).
//
// Usage:
//   const { plans, caPlans, clientPlans, loading, error, getPlanByCode,
//           getEffectivePrice, getSavingPct } = usePlans();
// =============================================================================

import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

// ── Module-level cache — fetched once per app session, not per component ──────
let _cache = null;        // resolved plan array
let _fetchPromise = null; // in-flight promise (prevents duplicate requests)

// ── Derive the CSS category key from plan_code ────────────────────────────────
// Used for tier badge CSS classes (tier-starter, tier-growth, etc.)
// plan_code is deterministic so we don't need a DB column for this.
function categoryFromCode(planCode) {
  const map = {
    ca_starter:        'starter',
    ca_growth:         'growth',
    ca_growth_pro:     'enterprise',
    client_starter:    'client_starter',
    client_growth:     'client_growth',
    client_growth_pro: 'client_growth_pro',
  };
  return map[planCode] || planCode;
}

// ── Normalise a raw DB row into the shape components expect ───────────────────
// FIX: was referencing `r` (undefined) — parameter is `raw` throughout.
// Added: category, cta, maxClients, maxTeamMembersActivated for LoginPage + Profile.
function normalizePlan(raw) {
  const maxC = raw.max_clients      ?? null;
  const maxT = raw.max_team_members ?? null;

  return {
    // ── Identity ──────────────────────────────────────────────────────────────
    planCode: raw.plan_code,
    name:     raw.plan_name,
    planType: raw.plan_type,   // 'ca' | 'client'

    // category drives tier-* CSS classes and TIER_DISPLAY lookups.
    // LoginPage:  `tier-${plan.category}` CSS classes  → needs short key ('starter')
    // Profile.jsx: tierStyle[plan.category]             → needs short key ('starter')
    category: categoryFromCode(raw.plan_code),

    // ── Pricing ───────────────────────────────────────────────────────────────
    priceMonthly:     raw.price_monthly_rate  ?? raw.price_monthly ?? 0,
    priceAnnual:      raw.price_annual_rate   ?? 0,
    priceMonthlyBulk: raw.price_monthly_bulk  ?? 0,
    priceAnnualBulk:  raw.price_annual_bulk   ?? 0,
    bulkThreshold:    raw.bulk_threshold      ?? 999999,

    // ── Billing metadata ──────────────────────────────────────────────────────
    billingUnit:  raw.billing_unit,
    durationUnit: raw.duration_unit ?? '',

    // ── Feature lists (shown on pricing cards) ────────────────────────────────
    // DB uses _json columns; fall back to legacy column names if present.
    ruleList:         raw.rule_list_json          ?? raw.rule_list          ?? [],
    ruleListDisabled: raw.rule_list_disabled_json ?? raw.rule_list_disabled ?? [],

    // ── Feature gates (backend enforcement) ───────────────────────────────────
    // DB shape: { key: { enabled: bool } } OR legacy flat: { key: bool }
    features: raw.features_json ?? raw.features ?? {},

    // ── Limits ────────────────────────────────────────────────────────────────
    maxClients:              maxC,
    maxClientsActivated:     maxC == null ? 'Unlimited' : maxC,
    maxTeamMembers:          maxT,
    maxTeamMembersActivated: maxT == null ? 'Unlimited' : maxT,
    minClients:              raw.min_clients ?? 1,

    // ── Display ───────────────────────────────────────────────────────────────
    featured:  raw.featured   ?? false,
    trialDays: raw.trial_days ?? 30,
    caption:   raw.caption    ?? '',
    priceDisplay: raw.price_display || (raw.price_monthly_rate > 0 ? `₹${Math.round(raw.price_monthly_rate).toLocaleString('en-IN')}` : 'Free'),
    ctaLabel:  raw.cta_label  ?? 'Start free trial →',
    ctaStyle:  raw.cta_style  ?? 'outline', // 'outline' | 'blue' | 'dark'
    // LoginPage uses plan.cta (short alias for ctaLabel)
    cta:       raw.cta_label  ?? 'Start free trial →',

    sortOrder: raw.sort_order ?? 0,
  };
}

// ── Fetch + cache ─────────────────────────────────────────────────────────────
function fetchPlans() {
  if (_cache) return Promise.resolve(_cache);
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = apiFetch('/subscriptions/plans')
    .then(rows => {
      if (!Array.isArray(rows)) throw new Error('Invalid plans response');
      _cache = rows
        .map(normalizePlan)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      _fetchPromise = null;
      return _cache;
    })
    .catch(err => {
      _fetchPromise = null;
      throw err;
    });

  return _fetchPromise;
}

// ── Invalidate cache (call after PricingAdmin saves a plan change) ────────────
export function invalidatePlansCache() {
  _cache = null;
  _fetchPromise = null;
}

// ── Price helpers — operate on normalised plan objects ────────────────────────

/**
 * Effective per-unit per-month price given billing cycle and seat count.
 * @param {object} plan      — normalised plan from usePlans
 * @param {'monthly'|'annual'} cycle
 * @param {number} unitCount
 * @returns {number}
 */
export function getEffectivePrice(plan, cycle, unitCount = 1) {
  if (!plan) return 0;
  const isBulk = (plan.bulkThreshold ?? 999999) < 999999 && unitCount > plan.bulkThreshold;
  if (cycle === 'annual') {
    return isBulk
      ? (plan.priceAnnualBulk  || plan.priceAnnual  || plan.priceMonthly || 0)
      : (plan.priceAnnual      || plan.priceMonthly || 0);
  }
  return isBulk
    ? (plan.priceMonthlyBulk || plan.priceMonthly || 0)
    : (plan.priceMonthly     || 0);
}

/**
 * Saving percentage: annual vs monthly rate.
 * @param {object} plan
 * @param {number} unitCount
 * @returns {number} e.g. 17
 */
export function getSavingPct(plan, unitCount = 1) {
  if (!plan || !plan.priceMonthly) return 0;
  const monthly = getEffectivePrice(plan, 'monthly', unitCount);
  const annual  = getEffectivePrice(plan, 'annual',  unitCount);
  if (!monthly) return 0;
  return Math.round((1 - annual / monthly) * 100);
}

// ── React hook ────────────────────────────────────────────────────────────────
export default function usePlans() {
  const [plans,   setPlans]   = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (_cache) {
      setPlans(_cache);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPlans()
      .then(p  => { setPlans(p);                                 setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load plans'); setLoading(false); });
  }, []);

  const caPlans     = plans.filter(p => p.planType === 'ca');
  const clientPlans = plans.filter(p => p.planType === 'client');

  function getPlanByCode(planCode) {
    return plans.find(p => p.planCode === planCode) || null;
  }

  return { plans, caPlans, clientPlans, loading, error, getPlanByCode, getEffectivePrice, getSavingPct };
}